import { useEffect, useState } from 'react';
import { ChevronLeft, Loader2, CheckCircle2, AlertCircle, Package, ShieldCheck } from 'lucide-react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getStripe } from '../../lib/stripe';
import { usePurchaseSessionPackage } from '../../hooks/useAPI';
import supabase from '../../lib/supabase';

interface PackageCheckoutScreenProps {
  packageId: string;
  coachId: string; // return-to on cancel
  onNavigate: (screen: any, params?: any) => void;
  onPurchased: () => void;
}

type PkgDetail = {
  id: string;
  name: string;
  sessions_included: number;
  price: number;
  currency: string;
  validity_weeks: number;
  lesson_type: string;
  environment: string;
};

export default function PackageCheckoutScreen({
  packageId, coachId, onNavigate, onPurchased,
}: PackageCheckoutScreenProps) {
  const { purchase, isLoading } = usePurchaseSessionPackage();
  const [pkg, setPkg] = useState<PkgDetail | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [creditId, setCreditId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from('session_packages')
        .select('id, name, sessions_included, price, currency, validity_weeks, lesson_type, environment')
        .eq('id', packageId)
        .single();
      if (data) setPkg(data as PkgDetail);

      const result = await purchase(packageId);
      if (result.error) {
        setError(result.error.message || 'Could not start purchase');
        return;
      }
      setCreditId(result.data?.creditId ?? null);
      setClientSecret(result.data?.clientSecret ?? null);
    })();
  }, [packageId]);

  if (success) {
    return (
      <div className="w-full h-screen bg-background flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <div className="w-20 h-20 rounded-full bg-[#D4F04A] mx-auto mb-5 flex items-center justify-center">
            <CheckCircle2 className="w-10 h-10 text-[#1A6B4F]" />
          </div>
          <h2 className="text-2xl mb-2">Package unlocked</h2>
          <p className="text-sm text-muted-foreground mb-6">
            {pkg?.sessions_included} sessions ready to book. Pick a time from the coach's profile.
          </p>
          <button
            onClick={onPurchased}
            className="h-11 px-6 rounded-xl bg-[#1A6B4F] text-white text-sm"
          >
            Back to coach
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-background pb-28">
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border px-4 py-3 flex items-center gap-3">
        <button
          onClick={() => onNavigate('coach', { id: coachId })}
          className="w-10 h-10 rounded-full hover:bg-secondary flex items-center justify-center"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-lg">Purchase package</h1>
          <p className="text-xs text-muted-foreground">Pay once, book many</p>
        </div>
      </div>

      <div className="px-6 py-5 space-y-4">
        {pkg && (
          <div className="bg-gradient-to-br from-[#0F1E36] to-[#16294A] rounded-2xl p-5 text-white">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#D4F04A] flex items-center justify-center flex-shrink-0">
                <Package className="w-5 h-5 text-[#1A6B4F]" />
              </div>
              <div>
                <div className="text-lg font-medium">{pkg.name}</div>
                <div className="text-sm opacity-80 mt-0.5 capitalize">
                  {pkg.sessions_included} × {pkg.lesson_type.replace('_', '-')} · {pkg.environment} · {pkg.validity_weeks}-week validity
                </div>
              </div>
            </div>
            <div className="h-px bg-white/15 my-3" />
            <div className="flex justify-between items-baseline">
              <span className="text-sm opacity-80">Total</span>
              <span className="text-xl font-semibold text-[#D4F04A]">
                {pkg.currency} {Number(pkg.price).toFixed(0)}
              </span>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-xl p-3 text-sm text-destructive flex gap-2">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            {error}
          </div>
        )}

        {clientSecret && creditId ? (
          <Elements stripe={getStripe()} options={{ clientSecret }}>
            <CardPayment
              onSuccess={async () => {
                // Wait for webhook to flip sessions_remaining.
                for (let i = 0; i < 10; i++) {
                  const { data } = await supabase
                    .from('session_credits')
                    .select('sessions_remaining')
                    .eq('id', creditId)
                    .single();
                  if ((data?.sessions_remaining ?? 0) > 0) break;
                  await new Promise((r) => setTimeout(r, 1000));
                }
                setSuccess(true);
              }}
              onError={setError}
            />
          </Elements>
        ) : (
          <div className="py-6 flex items-center justify-center text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
            {isLoading || !clientSecret ? 'Preparing purchase…' : 'Loading payment…'}
          </div>
        )}

        <div className="flex items-start gap-2 text-xs text-muted-foreground">
          <ShieldCheck className="w-4 h-4 text-[#1A6B4F] flex-shrink-0 mt-0.5" />
          Package credits expire {pkg?.validity_weeks ?? 15} weeks from purchase.
          Transferable to a friend or family member; no refunds.
        </div>
      </div>
    </div>
  );
}

function CardPayment({ onSuccess, onError }: { onSuccess: () => void; onError: (msg: string) => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);

  const pay = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true);
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements, redirect: 'if_required',
    });
    if (error) { onError(error.message || 'Payment failed'); setBusy(false); return; }
    if (paymentIntent?.status === 'succeeded') onSuccess();
    else { onError('Payment did not succeed'); setBusy(false); }
  };

  return (
    <form onSubmit={pay} className="space-y-3">
      <PaymentElement />
      <button
        type="submit"
        disabled={!stripe || busy}
        className="w-full h-12 rounded-xl bg-[#1A6B4F] hover:bg-[#14563F] text-white text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2"
      >
        {busy && <Loader2 className="w-4 h-4 animate-spin" />}
        {busy ? 'Processing…' : 'Pay now'}
      </button>
    </form>
  );
}
